import clsx from 'clsx';
import A from 'components/atoms/A/A';
import Card from 'components/atoms/Card/Card';
import Txt from 'components/atoms/Txt/Txt';
import { SurveyCard_survey$key } from 'components/molecules/SurveyCard/__generated__/SurveyCard_survey.graphql';
import { useTrackingTrigger } from 'components/templates/ThirdPartyTrackingProvider/ThirdPartyTrackingProvider';
import { useRouter } from 'next/router';
import { ReactElement, useEffect, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import HotIcon from './assets/hot.svg';
import PendingIcon from './assets/pending.svg';
import SurveyBackground from './assets/survey-bg.svg';
import { findSurveyType } from './SURVEY_TYPES';
import classes from './SurveyCard.module.scss';
import WebcamIcon from './assets/webcam.svg';
import useShowAdmin from 'hooks/useShowAdmin';

export type Props = {
  className?: string;
  surveyRef: SurveyCard_survey$key;
};

const fragmentSpec = graphql`
  fragment SurveyCard_survey on Survey {
    id
    iconUrl
    rewardAmount {
      subUnitAmount
    }
    lockedInfo {
      title
    }
    title
    url
    status
    supportId
    durationDetails {
      providerDuration
    }
    tags
    adminData {
      rankScore
      surveyGuid
    }
  }
`;

function SurveyCard({ className, surveyRef }: Props): ReactElement {
  const survey = useFragment(fragmentSpec, surveyRef);
  const trackingTrigger = useTrackingTrigger();
  const router = useRouter();

  /**
   * If the appropriate query parameters are present, automatically open the survey.
   */
  useEffect(() => {
    if (
      router.query.direct_to_survey &&
      router.query.survey_id &&
      router.query.direct_to_survey === 'true' &&
      router.query.survey_id === survey.id &&
      survey.url
    ) {
      const params = new URLSearchParams(router.query as Record<string, string>);
      params.delete('direct_to_survey');
      params.delete('survey_id');
      router
        .replace({ pathname: router.pathname, query: params.toString() }, undefined, {
          shallow: true,
        })
        .then(() => {
          window.location.href = survey.url as string;
        });
    }
  }, [survey.url, survey.id, router]);

  function getTagImage() {
    if (survey.status === 'PENDING') {
      return <PendingIcon className={classes.tagImage} />;
    } else if (survey.tags.includes('hot')) {
      return <HotIcon color="red" className={classes.tagImage} />;
    } else {
      return null;
    }
  }

  const showAdmin = useShowAdmin();

  function trackSurveyStart() {
    trackingTrigger();
  }

  const tagImage = getTagImage();
  const type = findSurveyType(survey.tags);
  const TypeIcon = type.icon;
  const content = (
    <>
      <div className={classes.supportingInfo}>
        {tagImage && <div className={classes.badge}>{tagImage}</div>}
        <div className={classes.code}>
          {survey.tags.includes('screener') ? '' : survey.supportId || survey.id.substring(0, 8)}
        </div>
        {survey.tags.includes('webcam') && (
          <div className={classes.webcam}>
            <WebcamIcon />
          </div>
        )}

        <div className={classes.duration}>{survey.durationDetails.providerDuration} min</div>
      </div>
      {survey.adminData && showAdmin && (
        <div className={classes.primaryContent}>
          {survey.adminData?.surveyGuid && (
            <div>
              <Txt>guid: {survey.adminData.surveyGuid}</Txt>
            </div>
          )}
          {survey?.adminData?.rankScore && (
            <div>
              <Txt>score: {survey.adminData.rankScore.toString()}</Txt>
            </div>
          )}
        </div>
      )}
      <div className={classes.primaryContent}>
        <TypeIcon className={classes.surveyIcon} />
        {survey.tags.includes('screener') ? (
          <Txt className={classes.title} component="div">
            Fill in your profile
          </Txt>
        ) : (
          <Txt className={classes.title} skipTranslation>
            {survey.title}
          </Txt>
        )}
        {(survey.rewardAmount?.subUnitAmount || 0) > 0 && (
          <div className={classes.points}>{survey.rewardAmount?.subUnitAmount}</div>
        )}
      </div>
      <div className={classes.action}>
        {survey.url && !survey.lockedInfo?.title ? (
          <Txt>Take Survey</Txt>
        ) : (
          <Txt skipTranslation>{survey.lockedInfo?.title}</Txt>
        )}
      </div>
    </>
  );

  return (
    <Card className={clsx(classes.root, className)}>
      <SurveyBackground className={clsx(classes.background)} />
      {survey.url ? (
        <A href={survey.url} className={classes.link} target="_blank" onClick={trackSurveyStart}>
          {content}
        </A>
      ) : (
        <div className={classes.link}>{content}</div>
      )}
    </Card>
  );
}

export default SurveyCard;
